/**
 * Painel de colunas do kanban — compartilhado por Requisições de Compra e
 * Requisições de Separação. A lista de situações entra por prop
 * (`situacoes`: [{ value, label, tone }]). Redesenho ADR-0069: cada coluna
 * tem toggle "Visível" (coluna oculta vira tira recolhida no quadro), as
 * situações viram chips marcáveis e há "Restaurar padrão" quando o módulo
 * passa `padrao`.
 */
import React, { useEffect, useState } from "react";
import { FiArrowDown, FiArrowUp, FiCheck, FiPlus, FiTrash2 } from "react-icons/fi";
import styled from "styled-components";

import SlideOverPanel from "../../../../components/SlideOverPanel";
import { getToneTokens } from "../../../../styles/shared";

const Card = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px;
  background: var(--surface);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Linha = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
`;

const Swatch = styled.input`
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: none;
  cursor: pointer;
  flex: 0 0 auto;
`;

const Titulo = styled.input`
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 13px;
`;

const Toggle = styled.button`
  width: 30px;
  height: 18px;
  border-radius: 999px;
  border: none;
  padding: 0;
  position: relative;
  cursor: pointer;
  background: ${({ $on }) => ($on ? "var(--accent)" : "var(--border-strong)")};
  transition: background .15s ease;
  flex: 0 0 auto;
  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${({ $on }) => ($on ? 14 : 2)}px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    transition: left .15s ease;
  }
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $on, $tone }) => ($on ? getToneTokens(null, $tone).border : "var(--border)")};
  background: ${({ $on, $tone }) => ($on ? getToneTokens(null, $tone).background : "var(--surface)")};
  color: ${({ $on, $tone }) => ($on ? getToneTokens(null, $tone).text : "var(--ink-3)")};
`;

const Ghost = styled.button`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-3);
  cursor: pointer;
  &:hover:not(:disabled) { color: var(--ink); border-color: var(--border-strong); }
  &:disabled { opacity: .4; cursor: not-allowed; }
  &[data-perigo="true"]:hover { background: var(--danger-bg); color: var(--danger-text); border-color: var(--danger-border); }
`;

const NovaBtn = styled.button`
  height: 36px;
  border-radius: 10px;
  border: 1px dashed var(--border-strong);
  background: transparent;
  color: var(--ink-3);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:hover { color: var(--ink); border-color: var(--accent); }
`;

function novaColuna() {
  return {
    id: `col_${Date.now().toString(36)}`,
    titulo: "Nova coluna",
    cor: "#6b7280",
    situacoes: [],
    visivel: true,
  };
}

export default function ColunasPanel({ open, colunas, situacoes, onClose, onSalvar, padrao }) {
  const [cols, setCols] = useState([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) {
      setCols((colunas || []).map((c) => ({
        ...c, visivel: c.visivel !== false, situacoes: [...(c.situacoes || [])],
      })));
    }
  }, [open, colunas]);

  function patch(i, campo, valor) {
    setCols((cs) => cs.map((c, idx) => (idx === i ? { ...c, [campo]: valor } : c)));
  }

  function toggleSituacao(i, sit) {
    setCols((cs) => cs.map((c, idx) => {
      if (idx !== i) return c;
      const tem = c.situacoes.includes(sit);
      return { ...c, situacoes: tem ? c.situacoes.filter((s) => s !== sit) : [...c.situacoes, sit] };
    }));
  }

  function mover(i, delta) {
    setCols((cs) => {
      const j = i + delta;
      if (j < 0 || j >= cs.length) return cs;
      const novo = [...cs];
      [novo[i], novo[j]] = [novo[j], novo[i]];
      return novo;
    });
  }

  function remover(i) {
    setCols((cs) => cs.filter((_, idx) => idx !== i));
  }

  async function salvar() {
    setSalvando(true);
    try {
      await onSalvar(cols);
    } finally {
      setSalvando(false);
    }
  }

  const semColuna = situacoes.filter((s) => !cols.some((c) => c.situacoes.includes(s.value)));

  const footer = (
    <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
      {padrao ? (
        <button className="btn btn--secondary btn--sm" type="button" onClick={() => setCols(padrao.map((c) => ({ ...c, situacoes: [...c.situacoes] })))}>
          Restaurar padrão
        </button>
      ) : <span />}
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn--secondary" onClick={onClose}>Cancelar</button>
        <button className="btn btn--primary" onClick={salvar} disabled={salvando || cols.length === 0}>
          {salvando ? "Salvando…" : "Salvar colunas"}
        </button>
      </div>
    </div>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Colunas do quadro"
      subtitle="Cada coluna agrupa um ou mais status. Status sem coluna não aparecem no quadro."
      width="min(94vw, 520px)"
      footer={footer}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {cols.map((c, i) => (
          <Card key={c.id}>
            <Linha>
              <span style={{ color: "var(--ink-4)", cursor: "grab", fontSize: 14, lineHeight: 1 }} aria-hidden>⋮⋮</span>
              <Swatch
                type="color"
                value={c.cor || "#6b7280"}
                onChange={(e) => patch(i, "cor", e.target.value)}
                aria-label="Cor da coluna"
              />
              <Titulo
                value={c.titulo}
                onChange={(e) => patch(i, "titulo", e.target.value)}
                aria-label="Título da coluna"
              />
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                Visível
                <Toggle type="button" role="switch" aria-checked={c.visivel} $on={c.visivel} onClick={() => patch(i, "visivel", !c.visivel)} />
              </label>
              <Ghost type="button" onClick={() => mover(i, -1)} disabled={i === 0} aria-label="Mover para cima"><FiArrowUp size={13} /></Ghost>
              <Ghost type="button" onClick={() => mover(i, 1)} disabled={i === cols.length - 1} aria-label="Mover para baixo"><FiArrowDown size={13} /></Ghost>
              <Ghost type="button" data-perigo="true" onClick={() => remover(i)} aria-label="Remover coluna"><FiTrash2 size={13} /></Ghost>
            </Linha>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {situacoes.map((s) => {
                const on = c.situacoes.includes(s.value);
                return (
                  <Chip key={s.value} type="button" $on={on} $tone={s.tone || "neutral"} onClick={() => toggleSituacao(i, s.value)} aria-pressed={on}>
                    {on ? <FiCheck size={12} /> : null}
                    {s.label}
                  </Chip>
                );
              })}
            </div>
          </Card>
        ))}
        <NovaBtn type="button" onClick={() => setCols((cs) => [...cs, novaColuna()])}>
          <FiPlus size={14} /> Nova coluna
        </NovaBtn>
        {semColuna.length ? (
          <div style={{ fontSize: 12, color: "var(--warning-text)" }}>
            Sem coluna (não aparecem no quadro): {semColuna.map((s) => s.label).join(", ")}.
          </div>
        ) : null}
      </div>
    </SlideOverPanel>
  );
}
